import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Tree } from 'antd';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
} from '../../thunks/problem_types_thunks';
import OWTag from '../common/OWTag';
import { nullSafeGet, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { withUserType } from '../withUserType';

const TARGET_COLLECTION = 'problemTypeHierarchicalDisplay';

interface ProblemTypeNode {
	id: number;
	name: string;
	hierarchyName: string;
	children?: ProblemTypeNode[];
}

// Module-level cache keyed by user email. One entry per user, cleared on page reload.
// This ensures a single network call regardless of how many component instances mount.
const cache = new Map<
	string,
	{ promise: Promise<ProblemTypeNode[]> | null; data: ProblemTypeNode[] | null }
>();

interface ProblemTypeHierarchicalDisplayProps {
	problemTypeIds: number[];
	collapsed?: boolean;
	displayMode?: 'tree' | 'pill';
	userType: string;
	userEmail: string;
	fetchProblemTypes: (
		params: any,
		targetCollectionName: string,
		pagination: any
	) => Promise<ProblemTypeNode[]>;
}

const collapseSelectedIds = (nodes: ProblemTypeNode[], selectedIds: number[]): number[] => {
	let result = [...selectedIds];
	const processNode = (node: ProblemTypeNode) => {
		if (node.children && node.children.length > 0) {
			node.children.forEach((child) => processNode(child));
			const allDirectChildrenInResult = node.children.every((child) => result.includes(child.id));
			if (allDirectChildrenInResult) {
				const childIds = node.children.map((child) => child.id);
				result = result.filter((id) => !childIds.includes(id));
				if (!result.includes(node.id)) {
					result.push(node.id);
				}
			}
		}
	};
	nodes.forEach((node) => processNode(node));
	return result;
};

const findNodeById = (nodes: ProblemTypeNode[], id: number): ProblemTypeNode | undefined => {
	for (const node of nodes) {
		if (node.id === id) return node;
		if (node.children) {
			const found = findNodeById(node.children, id);
			if (found) return found;
		}
	}
	return undefined;
};

const filterTree = (nodes: ProblemTypeNode[], selectedIds: number[]): any[] => {
	return nodes.reduce((acc, node) => {
		const isSelected = selectedIds.includes(node.id);
		const filteredChildren = filterTree(node.children || [], selectedIds);
		if (isSelected || filteredChildren.length > 0) {
			acc.push({
				key: String(node.id),
				title: node.name,
				children: filteredChildren.length > 0 ? filteredChildren : undefined,
			});
		}
		return acc;
	}, []);
};

const getAllParentKeys = (nodes: any[]): string[] => {
	return nodes.reduce((acc, node) => {
		if (node.children && node.children.length > 0) {
			acc.push(node.key);
			acc.push(...getAllParentKeys(node.children));
		}
		return acc;
	}, []);
};

const addCountToCollapsed = (nodes: any[], expandedKeys: Set<string>): any[] => {
	return nodes.map((node) => {
		const hasChildren = node.children && node.children.length > 0;
		const isExpanded = expandedKeys.has(node.key);
		const updatedChildren = hasChildren
			? addCountToCollapsed(node.children, expandedKeys)
			: undefined;
		return {
			...node,
			title:
				hasChildren && !isExpanded ? (
					<div className="flex flex-row items-center gap-1">
						{node.title} <span className="text-xs text-gray-400">({node.children.length})</span>
					</div>
				) : (
					node.title
				),
			children: updatedChildren,
		};
	});
};

const ProblemTypeHierarchicalDisplay: FC<ProblemTypeHierarchicalDisplayProps> = ({
	problemTypeIds,
	collapsed = false,
	displayMode = 'tree',
	userEmail,
	fetchProblemTypes,
}) => {
	const [treeData, setTreeData] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

	useEffect(() => {
		if (!collapsed && treeData.length > 0) {
			setExpandedKeys(getAllParentKeys(filterTree(treeData, problemTypeIds)));
		}
	}, [treeData, collapsed, problemTypeIds]);

	useEffect(() => {
		const cacheKey = userEmail;

		if (!cache.has(cacheKey)) {
			cache.set(cacheKey, { promise: null, data: null });
		}
		const entry = cache.get(cacheKey)!;

		if (entry.data) {
			setTreeData(entry.data);
			return;
		}

		setLoading(true);

		if (!entry.promise) {
			entry.promise = fetchProblemTypes({}, TARGET_COLLECTION, null);
		}

		entry.promise
			.then((data) => {
				entry.data = data;
				setTreeData(data);
			})
			.catch(() => {
				// Clear the cached promise so next mount will retry
				entry.promise = null;
			})
			.finally(() => setLoading(false));
	}, []);

	if (!problemTypeIds || problemTypeIds.length === 0) {
		return (
			<div className="inline-flex">
				<OWTag>All Problem Types</OWTag>
			</div>
		);
	}

	if (loading) {
		return null;
	}

	if (displayMode === 'pill') {
		const collapsedIds = collapseSelectedIds(treeData, problemTypeIds);
		const names = collapsedIds
			.map((id) => {
				const node = findNodeById(treeData, id);
				if (!node) return undefined;
				const hasChildren = node.children && node.children.length > 0;
				return hasChildren ? node.hierarchyName + ' - All' : node.hierarchyName;
			})
			.filter(Boolean) as string[];

		return (
			<div className="inline-flex flex-wrap gap-1">
				{names.map((name) => (
					<OWTag key={name}>{name}</OWTag>
				))}
			</div>
		);
	}

	const filteredTree = filterTree(treeData, problemTypeIds);

	if (filteredTree.length === 0) {
		return null;
	}

	const expandedKeysSet = new Set(expandedKeys);
	const displayTree = addCountToCollapsed(filteredTree, expandedKeysSet);

	return (
		<div onClick={stopDefaultEvents}>
			<Tree
				showLine={{ showLeafIcon: false }}
				selectable={false}
				treeData={displayTree}
				expandedKeys={expandedKeys}
				onExpand={(keys) => setExpandedKeys(keys as string[])}
			/>
		</div>
	);
};

const mapStateToProps = (state) => ({
	userEmail: nullSafeGet('currentUser.email', state.session),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchProblemTypes: (params, targetCollectionName, pagination) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier(params, targetCollectionName, pagination)
				: problemTypesHeirarchicalForBuyer(params, targetCollectionName, pagination)
		),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(ProblemTypeHierarchicalDisplay as any) as React.FC<{
		problemTypeIds: number[];
		collapsed?: boolean;
		displayMode?: 'tree' | 'pill';
	}>
);
